import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { StatusBadge } from '../components/shared/StatusBadge';
import { varianceAPI } from '../lib/api';
import { formatCurrency, formatPercent, CHART_COLORS, CHANNEL_NAMES } from '../lib/utils';

export default function VariancePage() {
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await varianceAPI.get(2024);
        setVariance(response.data);
      } catch (err) {
        console.error('Failed to fetch variance:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  // Prepare chart data
  const revenueVarianceData = variance?.revenue_variance?.map((v) => ({
    name: v.metric.replace('Revenue', '').trim() || 'Total',
    planned: v.planned / 10000000,
    actual: v.actual / 10000000,
    variance: v.variance_pct,
  })) || [];

  const costVarianceData = variance?.cost_variance?.map((v) => ({
    name: v.metric,
    planned: v.planned / 10000000,
    actual: v.actual / 10000000,
    variance: v.variance_pct,
    status: v.status,
  })) || [];

  return (
    <div className="space-y-6" data-testid="variance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Variance Analysis</h2>
          <p className="text-slate-500">{variance?.period} - AOP vs Actual Performance</p>
        </div>
        <div className="flex items-center gap-4">
          <StatusBadge status={variance?.summary?.overall_status} label="Overall Status" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* EBITDA Variance */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">EBITDA Variance</h3>
            <StatusBadge status={variance?.ebitda_variance?.status} showIcon={false} />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Planned</span>
              <span className="font-mono">{formatCurrency(variance?.ebitda_variance?.planned, { compact: true })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Actual</span>
              <span className="font-mono">{formatCurrency(variance?.ebitda_variance?.actual, { compact: true })}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-100">
              <span className="font-medium">Variance</span>
              <span className={`font-mono font-semibold ${variance?.ebitda_variance?.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatPercent(variance?.ebitda_variance?.variance_pct, { showSign: true })}
              </span>
            </div>
          </div>
        </div>

        {/* Key Concern */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-heading font-semibold text-amber-900">Key Concern</h3>
          </div>
          <p className="text-amber-800 font-medium">{variance?.summary?.key_concern}</p>
          <p className="text-sm text-amber-700 mt-2">{variance?.summary?.recommendation}</p>
        </div>

        {/* Opportunity */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading font-semibold text-emerald-900">Opportunity</h3>
          </div>
          <p className="text-emerald-800 font-medium">{variance?.summary?.opportunity}</p>
        </div>
      </div>

      {/* Revenue Variance Chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Revenue Variance by Line Item (₹Cr)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={revenueVarianceData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(v) => `₹${v}Cr`} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} width={100} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
              }}
              formatter={(value) => [`₹${value.toFixed(2)}Cr`, '']}
            />
            <Bar dataKey="planned" fill="#E2E8F0" name="Planned" radius={[0, 4, 4, 0]} />
            <Bar dataKey="actual" name="Actual" radius={[0, 4, 4, 0]}>
              {revenueVarianceData.map((entry, index) => (
                <Cell key={index} fill={entry.variance >= 0 ? CHART_COLORS[1] : CHART_COLORS[3]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Variance Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Revenue Breakdown</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Planned</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variance?.revenue_variance?.map((item, index) => (
                <TableRow key={index} className="hover:bg-slate-50">
                  <TableCell className="font-medium">{item.metric}</TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(item.planned, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(item.actual, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono ${item.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatPercent(item.variance_pct, { showSign: true })}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusBadge status={item.status} showIcon={false} size="sm" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Cost Variance Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Cost Breakdown</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Planned</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variance?.cost_variance?.map((item, index) => (
                <TableRow key={index} className="hover:bg-slate-50">
                  <TableCell className="font-medium">{item.metric}</TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(item.planned, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(item.actual, { compact: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono ${item.variance <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatPercent(item.variance_pct, { showSign: true })}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusBadge status={item.status} showIcon={false} size="sm" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Channel ROAS Variance */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Channel ROAS Variance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {variance?.channel_variance?.map((channel, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                channel.status === 'on_track' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
              }`}
            >
              <p className="text-sm font-medium text-slate-900 capitalize">{channel.channel}</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-mono text-2xl font-bold">{channel.actual_roas}x</span>
                <span className={`text-sm font-mono ${channel.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {channel.variance >= 0 ? '+' : ''}{channel.variance}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Target: {channel.planned_roas}x</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
